import {
	makeStyles,
	Button,
	tokens,
	Skeleton,
	SkeletonItem,
	Input,
	Caption1
} from '@fluentui/react-components';
import { Add32Filled } from '@fluentui/react-icons';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useFetchReviewsQuery } from '../Api/Reviews/reviewSlice';
import useDebouncer from '../Hooks/useDebouncer';
import { useAppSelector } from '../pgStore';
import { filterByValue } from '../Shared/filterByText';
import Page from '../Shared/Page/Page';
import ReviewItem from './ReviewItem';

const ReviewSearch = () => {
	const styles = useStyles();
	const user = useAppSelector((state) => state.userSlice);
	const [searchText, setSearchText] = useState('');
	const [apiSearchText, setApiSearchText] = useState('');
	//TODO: I think using filterByValue here within the selectFromResult creates a new Array which needs to be memoized correctly
	const { reviews, isLoading } = useFetchReviewsQuery(undefined, {
		selectFromResult: ({ data, isLoading }) => ({
			reviews: filterByValue(data, apiSearchText),
			isLoading
		})
	});
	const navigator = useNavigate();

	useDebouncer(
		() => {
			setApiSearchText(searchText);
		},
		[searchText],
		400
	);

	return (
		<Page
			title='Reviews'
			actions={
				<>
					<Input
						type='text'
						value={searchText}
						onChange={(event) => {
							setSearchText(event.target.value);
						}}
						placeholder='Search...'
						style={{ width: '100%', minWidth: '300px' }}
						appearance='underline'
					/>
					{user.authenticated && (
						<Button
							type='button'
							appearance='primary'
							icon={<Add32Filled />}
							onClick={() => navigator('/reviews/create')}
						></Button>
					)}
				</>
			}
		>
			<div className={styles.root}>
				{isLoading && (
					<div className={styles.cardContainer}>
						{Array.from(Array(10)).map((_item, index) => (
							<Skeleton key={`review-skeleton-${String(index)}`}>
								<div className={styles.skeletonCard}>
									<div className={styles.skeletonCardHeader}>
										<SkeletonItem
											shape='circle'
											size={32}
										/>
										<SkeletonItem
											shape='rectangle'
											size={32}
										/>
									</div>
									<SkeletonItem
										shape='rectangle'
										size={128}
										style={{ height: '200px' }}
									/>
									<div className={styles.skeletonCardFooter}>
										<div style={{ width: '32px' }}>
											<SkeletonItem
												shape='rectangle'
												size={36}
											/>
										</div>
										<div style={{ width: '96px' }}>
											<SkeletonItem
												shape='rectangle'
												size={36}
											/>
										</div>
									</div>
								</div>
							</Skeleton>
						))}
					</div>
				)}

				{!isLoading && (
					<>
						{reviews?.map((review) => (
							<div className={styles.cardContainer}>
								<ReviewItem
									key={review.id}
									review={review}
								/>
							</div>
						))}
					</>
				)}

				{(reviews?.length ?? 0) < 1 && (
					<Caption1>No Reviews Found!</Caption1>
				)}
			</div>
		</Page>
	);
};

export default ReviewSearch;

const useStyles = makeStyles({
	root: {
		display: 'flex',
		flexWrap: 'wrap',
		rowGap: '20px',
		columnGap: '20px',
		justifyContent: 'center'
	},
	cardContainer: {},
	image: {
		backgroundColor: tokens.colorNeutralBackground2,
		width: '100%',
		maxHeight: '200px',
		overflow: 'hidden'
	},
	skeletonCard: {
		backgroundColor: tokens.colorNeutralBackground1,
		maxWidth: '800px',
		minWidth: '375px',
		height: '312px',
		display: 'flex',
		flexDirection: 'column',
		rowGap: '8px'
	},
	skeletonCardHeader: {
		display: 'flex',
		columnGap: '18px',
		padding: '8px'
	},
	skeletonCardFooter: {
		display: 'flex',
		justifyContent: 'space-between',
		padding: '4px 12px'
	}
});
